import React, { useEffect, useState, useMemo, memo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { List, Divider, ListItem, Typography } from '@mui/material'
import { LinkMenuItem } from './LinkMenuItem'
import { HeadMenuItem } from './HeadMenuItem'
import { EnvironmentInfo } from './EnvironmentInfo'
import { AdminMainMenu } from './AdminMainMenu'
import {
    faCopy,
    faList,
    faPlusCircle,
    faChevronCircleUp,
    faChevronCircleDown,
} from '@fortawesome/pro-duotone-svg-icons'
import { MainMenuItemsProps, DefDossierItem } from '../model/types'
import { useMainMenuItems } from '../hooks/useMainMenuItems'

const MainMenuItems: React.FC<MainMenuItemsProps> = ({ kedo, closing }) => {
    const history = useHistory()
    const [showOther, setShowOther] = useState(false)
    const [showHidden, setShowHidden] = useState(false)
    const [active, setActive] = useState<string>(() =>
        JSON.parse(sessionStorage.getItem('activeEntity') || '""')
    )

    const { main, other, hidden, fetchMenuItems } = useMainMenuItems(kedo)

    useEffect(() => {
        fetchMenuItems()
    }, [fetchMenuItems])

    const getResponsiveHandle = useCallback((close?: () => void) => {
        window.scrollTo(0, 0)
        if (close && window.innerWidth <= 576) close()
    }, [])

    const renderQueryItems = useCallback(
        (ddItem: DefDossierItem, hidden?: boolean) => {
            if (!ddItem.query_settings?.menu_queries) return null
            return ddItem.query_settings.menu_queries
                .sort((a, b) => a.rank - b.rank)
                .map((queryItem) => (
                    <LinkMenuItem
                        key={`${ddItem.id}-${queryItem.queryUuid}`}
                        to={`/defdossier/${ddItem.id}?dossierQuery=${queryItem.queryUuid}`}
                        hidden={hidden}
                        nested
                        exact
                        matchSearch
                        className={`link-item${hidden ? ' hidden' : ''}`}
                        onClick={() => getResponsiveHandle(closing)}
                    >
                        {queryItem.name}
                    </LinkMenuItem>
                ))
        },
        [closing, getResponsiveHandle]
    )

    const getAllowedMenuItems = useCallback(
        (items: DefDossierItem[], hidden?: boolean) => {
            if (!items.length) {
                return (
                    <ListItem>
                        <Typography variant="body2" color="text.secondary">
                            {kedo.t('No items found')}
                        </Typography>
                    </ListItem>
                )
            }

            return items.map((item) => {
                const menuUrl = `/defdossier/${item.id}${
                    item.query_settings?.default_query
                        ? `?dossierQuery=${item.query_settings.default_query}`
                        : ''
                }`

                sessionStorage.setItem('activeEntity', JSON.stringify(active))

                return (
                    <React.Fragment key={item.id}>
                        <LinkMenuItem
                            to={menuUrl}
                            hidden={hidden}
                            exact
                            matchSearch
                            className={`menu-item link-item`}
                            onClick={() => {
                                setActive(item.id)
                                getResponsiveHandle(closing)
                            }}
                        >
                            {kedo.env().translateItem(item, 'defdossier', true)}
                        </LinkMenuItem>

                        {renderQueryItems(item, hidden)}
                    </React.Fragment>
                )
            })
        },
        [active, closing, kedo, renderQueryItems, getResponsiveHandle]
    )

    const mainMenuItems = useMemo(
        () => getAllowedMenuItems(main),
        [getAllowedMenuItems, main]
    )
    const otherMenuItems = useMemo(
        () => getAllowedMenuItems(other),
        [getAllowedMenuItems, other]
    )
    const hiddenMenuItems = useMemo(
        () => getAllowedMenuItems(hidden, true),
        [getAllowedMenuItems, hidden]
    )

    if (!kedo.env().getEnvironment()) {
        return <EnvironmentInfo kedo={kedo} />
    }

    return (
        <div>
            <AdminMainMenu
                kedo={kedo}
                close={() => getResponsiveHandle(closing)}
            />
            <nav className="dropdown" aria-label="Main Navigation">
                <List sx={{ p: 0 }}>
                    <HeadMenuItem
                        className="menu-item"
                        label={kedo.t('main_menu')}
                        title={kedo.t('New entity')}
                        icon={faCopy}
                        actionIcon={
                            kedo.isAdminOrEnvironmentAdmin()
                                ? faPlusCircle
                                : undefined
                        }
                        onClickIcon={
                            kedo.isAdminOrEnvironmentAdmin()
                                ? () =>
                                      history.push('/defdossier/edit/0', {
                                          id: 0,
                                          blub: (Date.now() % 1000) / 1000,
                                      })
                                : undefined
                        }
                    />
                    <Divider component="li" aria-hidden="true" />
                </List>
                {kedo.env().hasModule('jwwmo') && (
                    <List sx={{ p: 0 }}>
                        <LinkMenuItem
                            key={`mod-jwwmo`}
                            to="/module/jwwmo/toewijzingen"
                            className="menu-item link-item"
                            onClick={() => {
                                //Add link here
                            }}
                        >
                            {kedo.t('Toewijzingen')}
                        </LinkMenuItem>
                    </List>
                )}
                <List sx={{ p: 0 }}>{mainMenuItems}</List>
            </nav>

            {other.length > 0 && (
                <nav className="dropdown" aria-label="Other Navigation">
                    <List sx={{ p: 0 }}>
                        <Divider component="li" aria-hidden="true" />
                        <HeadMenuItem
                            className="menu-item other"
                            label={kedo.t('Other')}
                            icon={faList}
                            toggleIcon={{
                                open: faChevronCircleUp,
                                closed: faChevronCircleDown,
                            }}
                            isOpen={showOther}
                            onClick={() => setShowOther(!showOther)}
                        />
                        <Divider component="li" aria-hidden="true" />
                    </List>
                    {showOther && <List sx={{ p: 0 }}>{otherMenuItems}</List>}
                </nav>
            )}

            {(kedo.isAdminOrEnvironmentAdmin() ||
                kedo.env().hasCredential('hidden_menu')) &&
                hidden.length > 0 && (
                    <nav className="dropdown" aria-label="Hidden Navigation">
                        <List sx={{ p: 0 }}>
                            <HeadMenuItem
                                className="menu-item other"
                                label={kedo.t('Hidden')}
                                icon={faList}
                                toggleIcon={{
                                    open: faChevronCircleUp,
                                    closed: faChevronCircleDown,
                                }}
                                isOpen={showHidden}
                                onClick={() => setShowHidden(!showHidden)}
                            />
                            <Divider component="li" aria-hidden="true" />
                        </List>
                        {showHidden && (
                            <List sx={{ p: 0 }}>{hiddenMenuItems}</List>
                        )}
                    </nav>
                )}
        </div>
    )
}

export default memo(MainMenuItems)
